/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./WorkItemEditDrawer.css";
import {
  updateWorkItem,
} from "../../api/workItems";

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  complete: "Complete & awaiting verification",
  verified: "Verified",
};

const STATUS_OPTIONS = {
  open: [
    "open",
    "in_progress",
    "blocked",
  ],
  in_progress: [
    "in_progress",
    "blocked",
    "complete",
  ],
  blocked: [
    "blocked",
    "in_progress",
    "complete",
  ],
  complete: [
    "complete",
    "in_progress",
  ],
  verified: [
    "verified",
  ],
};

export default function WorkItemEditDrawer({
  workItem,
  onClose,
  onUpdated,
}) {
  const drawerRef = useRef(null);

  const [status, setStatus] =
    useState("open");

  const [
    completionNotes,
    setCompletionNotes,
  ] = useState("");

  const [
    evidenceUrls,
    setEvidenceUrls,
  ] = useState([]);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const isVerified = Boolean(
    workItem?.status === "verified" ||
      workItem?.verified_at
  );

  const availableStatuses = useMemo(() => {
    if (!workItem) {
      return [];
    }

    const options =
      STATUS_OPTIONS[status] || [
        status || workItem.status,
      ];

    /*
     * Ensure the currently selected status remains
     * visible even if the server returned an unexpected
     * transition state.
     */
    if (
      status &&
      !options.includes(status)
    ) {
      return [
        status,
        ...options,
      ];
    }

    return options;
  }, [
    workItem,
    status,
  ]);

  useEffect(() => {
    if (!workItem) {
      return;
    }

    setStatus(
      workItem.status || "open"
    );

    setCompletionNotes(
      workItem.completion_notes || ""
    );

    setEvidenceUrls(
      Array.isArray(
        workItem.completion_evidence_urls
      )
        ? workItem.completion_evidence_urls
        : []
    );

    setError("");
    setSaving(false);
  }, [workItem]);

  useEffect(() => {
    if (!workItem) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        onClose();
      }
    }

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    workItem,
    onClose,
    saving,
  ]);

  useEffect(() => {
    if (
      workItem &&
      drawerRef.current
    ) {
      drawerRef.current.focus();
    }
  }, [workItem]);

  if (!workItem) {
    return null;
  }

  const hasChanges =
    status !== workItem.status ||
    completionNotes.trim() !==
      (
        workItem.completion_notes || ""
      ).trim() ||
    !arraysEqual(
      normalizeUrls(evidenceUrls),
      normalizeUrls(
        workItem
          .completion_evidence_urls || []
      )
    );

  function validateForm() {
    const trimmedNotes =
      completionNotes.trim();

    if (
      status === "blocked" &&
      !trimmedNotes
    ) {
      return (
        "Add a note explaining what is " +
        "blocking this work item."
      );
    }

    if (
      status === "complete" &&
      !trimmedNotes
    ) {
      return (
        "Completion notes are required " +
        "before submitting this work item " +
        "for verification."
      );
    }

    const invalidUrl =
      evidenceUrls.find(
        (url) =>
          url.trim() &&
          !isValidUrl(url.trim())
      );

    if (invalidUrl) {
      return (
        `"${invalidUrl}" is not a valid ` +
        "evidence URL."
      );
    }

    return "";
  }

  async function handleSave(event) {
    event.preventDefault();

    if (
      saving ||
      isVerified
    ) {
      return;
    }

    const validationError =
      validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      status,
      completion_notes:
        completionNotes.trim() || null,
      completion_evidence_urls:
        normalizeUrls(evidenceUrls),
    };

    try {
      setSaving(true);
      setError("");

      const updatedWorkItem =
        await updateWorkItem(
          workItem.id,
          payload
        );

      onUpdated?.(
        updatedWorkItem
      );
    } catch (err) {
      console.error(
        "Failed to update work item",
        err
      );

      setError(
        getApiErrorMessage(
          err,
          "Failed to update the work item."
        )
      );
    } finally {
      setSaving(false);
    }
  }

  function handleAddEvidenceUrl() {
    setEvidenceUrls(
      (current) => [
        ...current,
        "",
      ]
    );
  }

  function handleEvidenceUrlChange(
    index,
    value
  ) {
    setEvidenceUrls(
      (current) =>
        current.map(
          (url, currentIndex) =>
            currentIndex === index
              ? value
              : url
        )
    );
  }

  function handleRemoveEvidenceUrl(
    index
  ) {
    setEvidenceUrls(
      (current) =>
        current.filter(
          (_, currentIndex) =>
            currentIndex !== index
        )
    );
  }

  return (
    <div
      className="work-item-drawer-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (
          event.target ===
            event.currentTarget &&
          !saving
        ) {
          onClose();
        }
      }}
    >
      <aside
        ref={drawerRef}
        className="work-item-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="work-item-drawer-title"
        tabIndex={-1}
      >
        <header className="work-item-drawer__header">
          <div>
            <p className="work-item-drawer__eyebrow">
              Corrective action
            </p>

            <h2 id="work-item-drawer-title">
              {workItem.title}
            </h2>

            <div className="work-item-drawer__context">
              {workItem.incident_number ? (
                <span>
                  {
                    workItem
                      .incident_number
                  }
                </span>
              ) : null}

              {workItem.department ? (
                <span>
                  {workItem.department}
                </span>
              ) : null}
            </div>
          </div>

          <button
            type="button"
            className="work-item-drawer__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close work item"
          >
            ×
          </button>
        </header>

        <form
          className="work-item-drawer__body"
          onSubmit={handleSave}
        >
          {error ? (
            <div
              className="work-item-drawer__error"
              role="alert"
            >
              {error}
            </div>
          ) : null}

          {isVerified ? (
            <div className="work-item-drawer__verified-banner">
              <strong>
                This work item has been
                verified.
              </strong>

              <span>
                Verified work items are
                read-only.
              </span>
            </div>
          ) : null}

          <section className="work-item-drawer__section">
            <h3>Work item</h3>

            {workItem.description ? (
              <p className="work-item-drawer__description">
                {workItem.description}
              </p>
            ) : (
              <p className="work-item-drawer__muted">
                No description provided.
              </p>
            )}

            <DefinitionList
              items={[
                {
                  label: "Priority",
                  value: formatValue(
                    workItem.priority
                  ),
                },
                {
                  label: "Assigned to",
                  value:
                    workItem
                      .assigned_to_name ||
                    workItem.owner_name ||
                    "Unassigned",
                },
                {
                  label: "Due date",
                  value: workItem.due_date
                    ? formatDate(
                        workItem.due_date
                      )
                    : "No due date",
                },
              ]}
            />
          </section>

          <section className="work-item-drawer__section">
            <h3>Progress update</h3>

            <label
              className="work-item-form-field"
              htmlFor="work-item-status"
            >
              <span>Status</span>

              <select
                id="work-item-status"
                value={status}
                onChange={(event) => {
                  setStatus(
                    event.target.value
                  );
                  setError("");
                }}
                disabled={
                  saving ||
                  isVerified
                }
              >
                {availableStatuses.map(
                  (statusOption) => (
                    <option
                      key={statusOption}
                      value={statusOption}
                    >
                      {
                        STATUS_LABELS[
                          statusOption
                        ] ||
                        formatValue(
                          statusOption
                        )
                      }
                    </option>
                  )
                )}
              </select>
            </label>

            <label
              className="work-item-form-field"
              htmlFor="completion-notes"
            >
              <span>
                {status === "blocked"
                  ? "Blocker details"
                  : status === "complete"
                    ? "Completion notes"
                    : "Progress notes"}
              </span>

              <textarea
                id="completion-notes"
                value={completionNotes}
                onChange={(event) => {
                  setCompletionNotes(
                    event.target.value
                  );
                  setError("");
                }}
                disabled={
                  saving ||
                  isVerified
                }
                rows={6}
                placeholder={
                  status === "blocked"
                    ? (
                        "Describe what is " +
                        "blocking progress and " +
                        "what help is needed."
                      )
                    : status === "complete"
                      ? (
                          "Describe what was " +
                          "completed and the " +
                          "result."
                        )
                      : (
                          "Add a progress update " +
                          "or relevant context."
                        )
                }
                required={
                  status === "blocked" ||
                  status === "complete"
                }
              />

              {(status === "blocked" ||
                status === "complete") ? (
                <small>
                  Required for this status.
                </small>
              ) : null}
            </label>
          </section>

          <section className="work-item-drawer__section">
            <div className="work-item-drawer__section-header">
              <div>
                <h3>
                  Completion evidence
                </h3>

                <p>
                  Add links to documents,
                  photos, reports, or other
                  supporting evidence.
                </p>
              </div>

              {!isVerified ? (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={
                    handleAddEvidenceUrl
                  }
                  disabled={saving}
                >
                  Add evidence
                </button>
              ) : null}
            </div>

            {evidenceUrls.length === 0 ? (
              <p className="work-item-drawer__muted">
                No evidence links added.
              </p>
            ) : (
              <div className="evidence-url-list">
                {evidenceUrls.map(
                  (url, index) => (
                    <div
                      className="evidence-url-row"
                      key={index}
                    >
                      <label
                        className="work-item-form-field"
                        htmlFor={
                          `evidence-url-${index}`
                        }
                      >
                        <span>
                          Evidence link{" "}
                          {index + 1}
                        </span>

                        <input
                          id={
                            `evidence-url-${index}`
                          }
                          type="url"
                          value={url}
                          onChange={(
                            event
                          ) =>
                            handleEvidenceUrlChange(
                              index,
                              event.target
                                .value
                            )
                          }
                          disabled={
                            saving ||
                            isVerified
                          }
                          placeholder="https://..."
                        />
                      </label>

                      {!isVerified ? (
                        <button
                          type="button"
                          className="evidence-url-row__remove"
                          onClick={() =>
                            handleRemoveEvidenceUrl(
                              index
                            )
                          }
                          disabled={saving}
                          aria-label={
                            `Remove evidence ` +
                            `link ${index + 1}`
                          }
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  )
                )}
              </div>
            )}
          </section>

          {isVerified ? (
            <section className="work-item-drawer__section">
              <h3>Verification</h3>

              <DefinitionList
                items={[
                  {
                    label: "Verified by",
                    value:
                      workItem
                        .verified_by_name ||
                      workItem
                        .verified_by_email ||
                      "Reviewer",
                  },
                  {
                    label: "Verified on",
                    value:
                      workItem.verified_at
                        ? formatDateTime(
                            workItem
                              .verified_at
                          )
                        : "—",
                  },
                  {
                    label:
                      "Verification notes",
                    value:
                      workItem
                        .verification_notes ||
                      "No verification notes.",
                  },
                ]}
              />
            </section>
          ) : null}

          <footer className="work-item-drawer__footer">
            <button
              type="button"
              className="secondary-button"
              onClick={onClose}
              disabled={saving}
            >
              {isVerified
                ? "Close"
                : "Cancel"}
            </button>

            {!isVerified ? (
              <button
                type="submit"
                className="primary-button"
                disabled={
                  saving ||
                  !hasChanges
                }
              >
                {saving
                  ? "Saving…"
                  : status === "complete"
                    ? (
                        "Submit for " +
                        "verification"
                      )
                    : "Save update"}
              </button>
            ) : null}
          </footer>
        </form>
      </aside>
    </div>
  );
}

function DefinitionList({
  items,
}) {
  return (
    <dl className="work-item-definition-list">
      {items.map((item) => (
        <div key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function normalizeUrls(urls) {
  return urls
    .map((url) => url.trim())
    .filter(Boolean);
}

function arraysEqual(
  left,
  right
) {
  if (
    left.length !== right.length
  ) {
    return false;
  }

  return left.every(
    (value, index) =>
      value === right[index]
  );
}

function isValidUrl(value) {
  try {
    const url = new URL(value);

    return (
      url.protocol === "http:" ||
      url.protocol === "https:"
    );
  } catch {
    return false;
  }
}

function getApiErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) =>
        item?.msg || String(item)
      )
      .join(" ");
  }

  return fallback;
}

function formatValue(value) {
  if (!value) {
    return "—";
  }

  return String(value)
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = parseDateValue(
    value
  );

  if (
    Number.isNaN(date.getTime())
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  ).format(date);
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (
    Number.isNaN(date.getTime())
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }
  ).format(date);
}

function parseDateValue(value) {
  /*
   * Date-only strings are parsed as local dates to
   * avoid timezone shifts showing the previous day.
   */
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(
      value
    )
  ) {
    const [
      year,
      month,
      day,
    ] = value
      .split("-")
      .map(Number);

    return new Date(
      year,
      month - 1,
      day
    );
  }

  return new Date(value);
}